import { useState, useCallback, useMemo, useEffect } from 'react';
import { Position } from 'reactflow';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {
  CaretDown,
  CircleWavyQuestion,
  Gear,
  Link,
  Trash,
  X,
} from '@phosphor-icons/react';
import IconButtonTooltip from '../UI/IconButtonTooltip/IconButtonTooltip';
import { HelpModal } from './HelpModal';
import InputHandle from '../UI/InputHandle/InputHandle';
import { isActiveEdge } from '../../util/findActiveHandle';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import useFlowsStore from '../../store/storeFlows';
import { useReactFlow } from 'reactflow';
import { FlowService } from '../../service/FlowService';
import { useAuthStore } from '../../store/storeAuth';
import type { ConnectedContentItem } from './connectedContentUtils';

interface UploadNewContentSectionProps {
  id: string;
  findColor?: { color: string };
  connectedContent: ConnectedContentItem[];
  onContentChange: (content: ConnectedContentItem[]) => void;
  edges: any[];
  isProcessing?: boolean;
  isNodeLoading?: boolean;
  applyFilters?: boolean;
  applyDimensions?: boolean;
  onApplyFiltersChange?: (value: boolean) => void;
  onApplyDimensionsChange?: (value: boolean) => void;
}

const UploadNewContentSection = ({
  id,
  findColor,
  connectedContent,
  onContentChange,
  edges,
  isProcessing = false,
  isNodeLoading = false,
  applyFilters = false,
  applyDimensions = false,
  onApplyFiltersChange,
  onApplyDimensionsChange,
}: UploadNewContentSectionProps) => {
  const [uploadCollapsed, setUploadCollapsed] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  const [filtersOn, setFiltersOn] = useState(applyFilters);
  const [dimensionsOn, setDimensionsOn] = useState(applyDimensions);

  useEffect(() => {
    setFiltersOn(applyFilters);
  }, [applyFilters]);

  useEffect(() => {
    setDimensionsOn(applyDimensions);
  }, [applyDimensions]);
  const { setNodes: setNodeState, setEdges } = useReactFlow();
  const { setSaving, setSchema, flowId, onEdgeDelete } = useFlowsStore(
    state => state,
  );
  const { user } = useAuthStore(state => state);

  const objectCallerData = useMemo(
    () => [...(user?.spaceObjects || []), ...(user?.objects || [])],
    [user?.spaceObjects, user?.objects],
  );

  const handleDeleteContent = useCallback(
    async (itemId: string) => {
      if (itemId?.includes('custom_') || itemId?.startsWith('content-')) {
        const newContent = connectedContent.filter(
          content => content.id !== itemId,
        );
        onContentChange(newContent);
        return;
      }

      const sourceId = itemId?.split('$')?.[0] || itemId;

      const edgesToDelete = edges.filter(
        edge =>
          edge.target === id &&
          edge.targetHandle === 'content' &&
          edge.source === sourceId,
      );

      if (edgesToDelete.length === 0 || !flowId) {
        const newContent = connectedContent.filter(
          content => content.id !== itemId,
        );
        onContentChange(newContent);
        return;
      }

      setDeletingIds(prev => new Set(prev).add(itemId));

      try {
        const edgesToDeleteIds = new Set(edgesToDelete.map(e => e.id));

        await Promise.all(
          edgesToDelete.map(edge =>
            FlowService.deleteEdge({
              flowId,
              edgeId: edge.id,
            }),
          ),
        );

        setEdges(eds => eds.filter(e => !edgesToDeleteIds.has(e.id)));

        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          flowId,
          changeType: 'delete-edges',
          deleteEdges: edgesToDelete,
          objectCallerData,
          setSchema,
        });

        if (onEdgeDelete) {
          onEdgeDelete(edgesToDelete, edgesToDelete[0]?.id);
        }
      } catch (error) {
        console.error('Failed to delete edge:', error);
        setDeletingIds(prev => {
          const next = new Set(prev);
          next.delete(itemId);
          return next;
        });
      }
    },
    [
      connectedContent,
      onContentChange,
      edges,
      id,
      flowId,
      setEdges,
      setNodeState,
      setSaving,
      setSchema,
      objectCallerData,
      onEdgeDelete,
    ],
  );

  const linkIndices = useMemo(() => {
    const indices = new Map<string, number>();
    let linkCount = 0;
    connectedContent.forEach(item => {
      if (item.type === 'link') {
        linkCount++;
        indices.set(item.id, linkCount);
      }
    });
    return indices;
  }, [connectedContent]);

  useEffect(() => {
    const contentIds = new Set(connectedContent.map(item => item.id));
    setDeletingIds(prev => {
      const next = new Set(prev);
      let changed = false;
      prev.forEach(id => {
        if (!contentIds.has(id)) {
          next.delete(id);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [connectedContent]);

  const handleToggleCollapse = useCallback((evt: React.MouseEvent) => {
    evt.stopPropagation();
    setUploadCollapsed(prev => !prev);
  }, []);

  return (
    <>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        gap='1rem'
        position='relative'
      >
        <InputHandle
          activeColor={findColor?.color}
          handleId='content'
          isActive={isActiveEdge(edges, id, 'content', 'target')}
          left={-26}
          top={'50%'}
          position={Position.Left}
          type='target'
        />

        <Stack direction='row' alignItems='center' spacing='0.625rem'>
          <CaretDown
            onClick={handleToggleCollapse}
            size={20}
            style={{
              rotate: uploadCollapsed ? '-90deg' : '0deg',
              transition: 'rotate 150ms ease-in',
              cursor: 'pointer',
            }}
          />

          <Typography fontWeight={600}>Upload new content</Typography>
        </Stack>

        <Stack direction='row' alignItems='center' gap='0.5rem'>
          <IconButtonTooltip title='Help' onClick={() => setHelpOpen(true)}>
            <CircleWavyQuestion size={16} color='#98A2B3' />
          </IconButtonTooltip>

          <IconButtonTooltip
            title='Settings'
            onClick={() => {
              if (isNodeLoading) return;
              setSettingsOpen(!settingsOpen);
            }}
          >
            <Gear
              color={isNodeLoading ? '#D0D5DD' : '#98A2B3'}
              size={16}
              style={{ cursor: isNodeLoading ? 'not-allowed' : 'pointer' }}
            />
          </IconButtonTooltip>
        </Stack>
      </Stack>

      {!uploadCollapsed && (
        <Box
          sx={{
            marginTop: '1rem',
            padding: '8px',
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            borderRadius: '4px',
            border: '1px solid #D0D5DD',
            background: '#FFF',
          }}
        >
          {isProcessing && (
            <Stack
              direction='row'
              alignItems='center'
              gap='0.5rem'
              sx={{
                padding: '0.5rem 0.75rem',
                backgroundColor: '#F2F4F7',
                borderRadius: '0.375rem',
              }}
            >
              <CircularProgress size={14} sx={{ color: '#475467' }} />
              <Typography sx={{ fontSize: '0.8125rem', color: '#475467' }}>
                Processing new uploaded files...
              </Typography>
            </Stack>
          )}
          {(() => {
            const visibleContent = connectedContent.filter(
              item => !deletingIds.has(item.id),
            );
            return visibleContent.length === 0 ? (
              <Typography
                sx={{
                  color: '#475467',
                  fontSize: '0.875rem',
                  textAlign: 'center',
                  lineHeight: '1.66',
                  letterSpacing: '0.4px',
                }}
              >
                Connected content will show up in here
              </Typography>
            ) : (
              visibleContent.map(item => {
                const linkIndex = linkIndices.get(item.id) || 0;
                const displayLabel =
                  item.label ||
                  (item.type === 'link' ? `Link ${linkIndex}` : 'Short Text');
                const displayValue =
                  item.type === 'text' && item.value
                    ? item.value.length > 30
                      ? `${item.value.substring(0, 30)}...`
                      : item.value
                    : null;

                return (
                  <Box
                    key={item.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      padding: '0.75rem',
                      backgroundColor: 'white',
                      borderRadius: '0.5rem',
                      border: '1px solid #D0D5DD',
                    }}
                  >
                    {item.type === 'link' && (
                      <Link size={16} color='#101828' weight='regular' />
                    )}

                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '3rem',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          color: '#101828',
                          fontWeight: item.type === 'link' ? 400 : 600,
                          textDecoration:
                            item.type === 'link' ? 'underline' : 'none',
                        }}
                      >
                        {displayLabel}
                      </Typography>
                      {displayValue && (
                        <Typography
                          sx={{
                            fontSize: '0.875rem',
                            color: '#101828',
                            fontWeight: 400,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {displayValue}
                        </Typography>
                      )}
                    </Box>

                    <IconButtonTooltip
                      title='Delete'
                      onClick={() => handleDeleteContent(item.id)}
                    >
                      <Trash size={16} color='#98A2B3' />
                    </IconButtonTooltip>
                  </Box>
                );
              })
            );
          })()}
        </Box>
      )}

      <Dialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          Upload Settings
          <IconButton
            size='small'
            onClick={() => setSettingsOpen(false)}
            sx={{ ml: 1 }}
          >
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack direction='column' gap='0.75rem' sx={{ paddingTop: '0.5rem' }}>
            <Typography
              sx={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#101828',
              }}
            >
              Apply to new uploads
            </Typography>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              sx={{ pl: '0.5rem' }}
            >
              <Typography sx={{ fontSize: '0.875rem', color: '#475467' }}>
                Filters
              </Typography>
              <Switch
                size='small'
                checked={filtersOn}
                onChange={e => {
                  setFiltersOn(e.target.checked);
                  onApplyFiltersChange?.(e.target.checked);
                }}
                inputProps={{
                  'aria-label': 'Apply active filters to new uploads',
                }}
              />
            </Stack>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              sx={{ pl: '0.5rem' }}
            >
              <Typography sx={{ fontSize: '0.875rem', color: '#475467' }}>
                Dimensions
              </Typography>
              <Switch
                size='small'
                checked={dimensionsOn}
                onChange={e => {
                  setDimensionsOn(e.target.checked);
                  onApplyDimensionsChange?.(e.target.checked);
                }}
                inputProps={{
                  'aria-label': 'Apply active dimensions to new uploads',
                }}
              />
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>

      <HelpModal
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        title='Upload new Content'
        description={`Connect content sources to this memory pod. Content linked via edges will be indexed and searchable.

Supported content types:
• Text: Direct text input or connections from text nodes
• Links: URLs that will be processed and indexed

Once connected, content appears in the list above. Delete individual items using the trash icon, or configure additional settings for how new content is processed (filters, dimensions).`}
      />
    </>
  );
};

export { UploadNewContentSection };
